import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import ErrorModal from "./ErrorModal";
import SnackbarProvider from "components/Snackbar/SnackbarProvider";
import TutorialItem from "components/Tutorial/components/TutorialItem";
import WarningSnackbar from "components/WarningSnackbar";
import BillingRecoveryBanner from "components/BillingRecoveryBanner";
import UpdateModal from "components/UpdateModal";
import FeatureAnnouncementPopup from "components/FeatureAnnouncementPopup";
import WebinarModal from "components/WebinarModal";
import PackageUpgradeModal, { UPGRADE_SCENARIO_MAP } from "components/PackageUpgradeModal";
import { useAppSelector } from "store";
import userService from "api/user/user.service";
import SideMenu from "components/SideMenu";
import MobileTopBar from "components/MobileTopBar";
import MobileNavDrawer from "components/MobileNavDrawer";
import MobileWarning from "components/MobileWarning";
import DashboardChatbot from "components/DashboardChatbot";
import DashboardTopBar from "components/DashboardTopBar";
import FeatureTourModal from "components/FeatureTourModal";
import NotificationToaster from "components/NotificationToaster";
import useWhatsNewBoot from "components/WhatsNewMenu/useWhatsNewBoot";
import useNotificationsBoot from "components/WhatsNewMenu/useNotificationsBoot";
import { useSharedReportsBoot } from "hooks/useSharedReports";
import { useMediaQuery } from "hooks/useMediaQuery";
import { cn } from "utils/cn";

const NOTIF_TYPE_PACKAGE_FORCE_UPDATE = "package_force_update";

interface Props {
  children: React.ReactNode;
  isSideMenuFixed?: boolean;
  sideMenuCollapsed?: boolean;
  hiddenHeader?: boolean;
  /** Force-hide the global top bar even on a standard shell page. */
  hideDashboardTopBar?: boolean;
  restrictOnMobile?: boolean;

  [key: string]: any;
}

const Layout = ({ children, isSideMenuFixed = true, sideMenuCollapsed, hiddenHeader, hideDashboardTopBar, restrictOnMobile }: Props) => {
  const { user, isAuthorized, isChatbotOpen, topBarMode } = useAppSelector((state) => state.common);
  const location = useLocation();
  const [upgradeScenario, setUpgradeScenario] = useState<typeof UPGRADE_SCENARIO_MAP[keyof typeof UPGRADE_SCENARIO_MAP] | null>(null);
  // Below 834px (xs + sm) swap the fixed sidebar for the mobile top bar + off-canvas drawer.
  const isMobileShell = useMediaQuery("(max-width: 833px)");
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Fetch the What's New feed + hero-tour map once the user is authorized (hook self-guards).
  useWhatsNewBoot();
  // Fetch the Notifications feed + keep its unread badge fresh (focus-sync + heartbeat) — REF-6306.
  useNotificationsBoot();
  // The reports shared with the user's team, once per user: the menu shows "Reports" only when there is one (REF-7271).
  useSharedReportsBoot();

  // Pages that opt out of the chatbot-open grid reductions so their fixed multi-column layouts stay intact.
  // Tech Pack detail's asset-panel + upload-area + view-tabs layout depends on the 4-col grid; reductions break the side-by-side flow.
  // Photoshoot Studio's shooting-type selection is a 3-up choice that loses its layout when squeezed to 1-up.
  const { pathname } = location;
  const isFixedGridPage =
    pathname === "/tech-pack" ||
    pathname.startsWith("/tech-pack/") ||
    pathname === "/photoshoot-studio" ||
    pathname.startsWith("/photoshoot-studio/") ||
    // The range-plan create wizard manages its own chatbot-open column counts (StepBrands / StepMoodboard),
    // so keep the blanket reduction off it — otherwise the target-market grid stacks to one column.
    pathname.startsWith("/collection-range-plans/new");
  const shouldReduceGrids = isChatbotOpen && !isFixedGridPage;

  // The global top bar (Academy / Ask Agent / bell / avatar) belongs on the standard shell pages only.
  // Focused/full-screen surfaces (editors, creation flows, reports, auth) already opt out of the shell —
  // they collapse the sidebar, drop it entirely, or hide the header — so use those flags for the default.
  // A page/sub-view can override via topBarMode ("agent" = Ask-Agent-only, "hidden", "full").
  const routeShowsBar = isSideMenuFixed && !sideMenuCollapsed && !hiddenHeader && !hideDashboardTopBar;
  const effectiveTopBarMode = topBarMode ?? (routeShowsBar ? "full" : "hidden");
  const showDashboardTopBar = isAuthorized && !isMobileShell && effectiveTopBarMode !== "hidden";

  useEffect(() => {
    if (!user.notifInfo) return;

    const upgradeNotif = user.notifInfo.find((n) => n.notif_type === NOTIF_TYPE_PACKAGE_FORCE_UPDATE && n.notif_status === 1);

    if (!upgradeNotif?.notif_modal_type) return;

    const scenario = UPGRADE_SCENARIO_MAP[upgradeNotif.notif_modal_type];

    if (scenario) {
      setUpgradeScenario(scenario);
    }
  }, [user.notifInfo]);

  const boxed = isAuthorized && isSideMenuFixed && !isMobileShell;

  return (
    <main
      className="bg-black flex flex-col"
      style={{
        minHeight: "100%",
      }}
    >
      <SnackbarProvider />
      <WarningSnackbar />
      <UpdateModal />
      <TutorialItem />
      <FeatureAnnouncementPopup />
      <WebinarModal />
      {upgradeScenario && (
        <PackageUpgradeModal
          show
          onClose={() => {
            if (user.id) {
              userService.updateForcePackage(user.id, { is_accepted: "accepted" });
            }
            setUpgradeScenario(null);
          }}
          scenario={upgradeScenario}
        />
      )}
      {/* <MaintenanceModal /> */}
      <div className={cn("flex flex-1", boxed && "bg-body-100")}>
        {isAuthorized && isSideMenuFixed && !isMobileShell && (
          <div className="sticky flex flex-shrink-0 h-screen top-0">
            <SideMenu defaultCollapsed={sideMenuCollapsed} />
          </div>
        )}
        {/* Desktop: this is the scroll container (sticky sidebar stays put alongside it).
            Mobile: must NOT be a second scroller — the sticky 52px MobileTopBar lives inside
            it, leaving a tiny overflow region that iOS touch grabs and rubber-bands instead of
            scrolling the page (snap-back-to-top bug). On mobile let the window be the sole
            scroller. */}
        {/* Beside the fixed menu, the page is a box (the full height, its left corners rounded, its own edge): the menu
            is the ground it sits on, with no border of its own. */}
        <div
          className={cn("flex-1 min-w-0", !isMobileShell && "overflow-y-auto", boxed && "h-screen rounded-l-2xl border-l border-black-300 bg-body")}
          data-chatbot-body="true"
          data-chatbot-open={shouldReduceGrids ? "true" : undefined}
        >
          {/* P7 Faz 1: the plan's payment failed — said on every page, above the bars (in the flow, so nothing overlaps). Not
              gated on `isAuthorized`: a payment problem can be exactly why the account lost its paid access. */}
          <BillingRecoveryBanner />
          {isAuthorized && isMobileShell && <MobileTopBar onMenuClick={() => setDrawerOpen(true)} />}
          {showDashboardTopBar && <DashboardTopBar mode={effectiveTopBarMode === "agent" ? "agent" : "full"} />}
          {restrictOnMobile && isMobileShell ? (
            <div className="flex min-h-[calc(100vh-52px)] items-center justify-center">
              <MobileWarning />
            </div>
          ) : (
            children
          )}
        </div>
        {isAuthorized && <DashboardChatbot />}
      </div>
      {isAuthorized && isMobileShell && <MobileNavDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />}
      <ErrorModal />
      <FeatureTourModal />
      {isAuthorized && <NotificationToaster />}
    </main>
  );
};

export default Layout;
